/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Column, Table } from "@tanstack/react-table";
import { TextInput, Checkbox } from "flowbite-react";

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  type,
  id,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  type: string;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <TextInput
      {...props}
      type="search"
      id={id}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      // size='sm'
    />
  );
}

export function Filter<T extends { [key: string]: any }>({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<T>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const isSubscriptionFilter = column.id === "subscriptionId";
  const selectedValues = Array.isArray(columnFilterValue)
    ? (columnFilterValue as string[])
    : [];
  const [isOpen, setIsOpen] = React.useState(false);
  const [openUpward, setOpenUpward] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const sortedUniqueValues = React.useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys())
            .filter((v) => v !== "" && v !== null && v !== undefined)
            .sort(),
    [column.getFacetedUniqueValues()]
  );
  // console.log(column.id, sortedUniqueValues, column.getFacetedUniqueValues().keys())
  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!isSubscriptionFilter) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Check if we should open upward
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // If there's less space below than above, or less than 300px below, open upward
        setOpenUpward(spaceBelow < 300 && spaceAbove > spaceBelow);
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isSubscriptionFilter]);

  if (isSubscriptionFilter) {

    const handleCheckboxChange = (value: string, checked: boolean) => {
      const newValues = checked
        ? [...selectedValues, value]
        : selectedValues.filter((v) => v !== value);
      column.setFilterValue(newValues.length ? newValues : undefined);
    };

    const handleSelectAll = () => {
      if (selectedValues.length === sortedUniqueValues.length) {
        column.setFilterValue(undefined);
      } else {
        column.setFilterValue(sortedUniqueValues);
      }
    };

    const getButtonText = () => {
      if (selectedValues.length === 0) {
        return `All (${sortedUniqueValues.length})`;
      }
      if (selectedValues.length === sortedUniqueValues.length) {
        return `All (${sortedUniqueValues.length})`;
      }
      return `${selectedValues.length} selected`;
    };

    return (
      <div className="relative w-full" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          <span className="truncate">{getButtonText()}</span>
          <svg
            className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {isOpen && (
          <div
            className={`absolute z-[9999] w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto ${
              openUpward ? "bottom-full mb-1" : "top-full mt-1"
            }`}
          >
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <label className="flex items-center cursor-pointer">
                <Checkbox
                  checked={
                    selectedValues.length === sortedUniqueValues.length &&
                    sortedUniqueValues.length > 0
                  }
                  onChange={handleSelectAll}
                />
                <span className="ml-2 text-sm font-semibold text-gray-700">
                  Select All
                </span>
              </label>
            </div>
            <div className="p-2 space-y-1">
              {sortedUniqueValues.map((value: any) => (
                <label
                  key={value}
                  className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <Checkbox
                    checked={selectedValues.includes(value)}
                    onChange={(e) =>
                      handleCheckboxChange(value, e.target.checked)
                    }
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{value}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return typeof firstValue === "number" ? (
    <div>
      <div>
        <DebouncedInput
          type="number"
          id={column.id}
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, value])
          }
          placeholder={`(${column.getFacetedMinMaxValues()?.[0]} - ${
            column.getFacetedMinMaxValues()?.[1]
          })`}
          className="shadow-sm"
        />
      </div>
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => {
          return <option value={value} key={value} />;
        })}
      </datalist>
      <DebouncedInput
        type="text"
        id={column.id}
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search (${sortedUniqueValues.length})`} //column.getFacetedUniqueValues().size
        className="shadow-sm"
        list={column.id + "list"}
      />
    </>
  );
}
